import React, { useState, useEffect } from 'react';
import {
    Select,
    MenuItem,
    Avatar,
    ListItemAvatar,
    IconButton,
    Typography,
    Box,
    InputBase,
    ListSubheader,
    SelectChangeEvent,
    FormControl,
    Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PauseCircleOutlineOutlinedIcon from '@mui/icons-material/PauseCircleOutlineOutlined';
import PlayCircleFilledWhiteOutlinedIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import { motion } from "framer-motion";

// Import or define VoiceData interface here
export interface VoiceData {
    _id: string;
    name: string;
    previewUrl: string;
    avatarUrl: string;
    labels: {
        gender: string;
        accent: string;
        [key: string]: any;
    };
    voice_id: string;
    [key: string]: any;
}

type VoiceSelectorProps = {
    voices: VoiceData[];
    handlePlayPauseVoice: (voice: VoiceData) => void;
    playingVoiceId: string | null;
    selectedVoice: string;
    onVoiceChange: (voiceId: string, voice?: VoiceData) => void;
};

const VoiceSelector: React.FC<VoiceSelectorProps> = ({
    voices,
    handlePlayPauseVoice,
    playingVoiceId,
    selectedVoice,
    onVoiceChange,
}) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [voiceDurations, setVoiceDurations] = useState<{ [id: string]: number }>({});

    const filteredVoices = voices && voices.filter(voice => {
        const nameMatch = voice?.name?.toLowerCase().includes(searchTerm.toLowerCase());
        const accentMatch = voice?.labels?.accent?.toLowerCase().includes(searchTerm.toLowerCase());
        return nameMatch || accentMatch;
    });

    const handleSelectChange = (event: SelectChangeEvent<string>) => {
        const selected = voices.find(v => v.voice_id === event.target.value);
        onVoiceChange(event.target.value, selected);
    };

    // More bars for a denser visualizer
    const barHeights = [30, 38, 24, 42, 28, 36, 20, 40, 26, 34, 22, 32, 30, 38, 24, 42, 28, 36, 20, 40, 26, 34, 22, 32, 30, 38, 24, 42];

    // Load duration for each voice
    useEffect(() => {
        filteredVoices.forEach((voice) => {
            if (!voiceDurations[voice._id] && voice.previewUrl) {
                const audio = new window.Audio(voice.previewUrl);
                audio.addEventListener('loadedmetadata', () => {
                    setVoiceDurations((prev) => ({ ...prev, [voice._id]: audio.duration }));
                });
            }
        });
    }, [filteredVoices, voiceDurations]);

    // Handle search input changes
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    // Prevent event bubbling for search interactions
    const handleSearchKeyDown = (event: React.KeyboardEvent) => {
        event.stopPropagation();
    };

    const handleSearchMouseDown = (event: React.MouseEvent) => {
        event.stopPropagation();
    };

    const handleSearchClick = (event: React.MouseEvent) => {
        event.stopPropagation();
    };

    return (
        <FormControl fullWidth className='voiceSelector'>
            <Select
                size="small"
                required
                value={selectedVoice}
                onChange={handleSelectChange}
                className="selectVoice"
                displayEmpty
                renderValue={(selected: string) => {
                    if (!selected) return <Typography variant='h6' gutterBottom className='uploadText'>Select a voice</Typography>;
                    const voice = voices && voices.find(v => v.voice_id === selected);
                    if (!voice) return <Typography variant='h6' gutterBottom className='uploadText'>Select a voice</Typography>;
                    return (
                        <Box className="menuSelected" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar className='avatarSelector'>
                                <img
                                    src={voice.avatarUrl}
                                    alt={voice.name}
                                    className="avatarImages"
                                />
                            </Avatar>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                <Typography variant="body2" className="showcaseSubtitle">
                                    {voice.name}
                                </Typography>
                                <div className='flagBox'>
                                    <img src={voice.avatarUrl} alt="flagIcon" className="flagImages" />
                                    <Typography variant="body2" className="showcaseSubDescriptionItalic">
                                        {voice.labels.accent}
                                    </Typography>
                                </div>
                            </Box>
                        </Box>
                    );
                }}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 350,
                            backgroundColor: '#1F2138',
                            width: 350,
                            overflowY: 'auto',
                            padding: '0px 20px'
                        },
                    },
                    // Prevent auto focus on menu items when typing
                    autoFocus: false,
                }}
            >
                <ListSubheader 
                    className='searchHeader'
                    disableSticky
                >
                    <Box className='headerBox'>
                        <Typography variant="subtitle1" className='showcaseDescription'>Select an Audio</Typography>
                        <Chip label={`${voices.length} Voices`} className="hedraStyle" size="medium" />
                    </Box>
                    <Box className='searchBox'>
                        <SearchIcon fontSize="small" />
                        <InputBase
                            placeholder="Search male and female voices..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                            onMouseDown={handleSearchMouseDown}
                            onClick={handleSearchClick}
                            sx={{ 
                                ml: 1, 
                                flex: 1, 
                                fontSize: 14,
                                '& input': {
                                    padding: '8px 0',
                                }
                            }}
                            fullWidth
                            inputProps={{ 
                                'aria-label': 'Search male and female voices...',
                                autoComplete: 'off',
                            }}
                            className='searchInput'
                            autoFocus={false}
                        />
                    </Box>
                </ListSubheader>

                {filteredVoices.length === 0 && (
                    <MenuItem disabled divider sx={{ justifyContent: 'center', color: '#aaa' }}>
                        No voices found
                    </MenuItem>
                )}
                
                {filteredVoices.map((voice) => (
                    <MenuItem
                        key={voice._id}
                        value={voice.voice_id} 
                        divider
                        className='menuSelected'
                        sx={{
                            background: 'linear-gradient(90deg, #383C6A 0%, #282B4E00 100%)',
                            marginBottom: '4px',
                            borderRadius: '8px',
                        }}
                        onClick={() => onVoiceChange(voice.voice_id, voice)}
                    >
                        <ListItemAvatar>
                            <Avatar 
                                className='avatarSelector'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePlayPauseVoice(voice);
                                }}
                            >
                                <img
                                    src={voice.avatarUrl}
                                    alt={voice.name}
                                    className="avatarImages"
                                />
                            </Avatar>
                        </ListItemAvatar>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexGrow: 1, width: '100%' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                {playingVoiceId === voice.voice_id ? (
                                    <Typography>
                                        {voiceDurations[voice._id]
                                            ? `${voiceDurations[voice._id].toFixed(2)}`
                                            : '...'}
                                    </Typography>
                                ) : (
                                    <>
                                        <Typography variant="body2" className="showcaseSubtitle">
                                            {voice.name}
                                        </Typography>
                                        <div className='flagBox'>
                                            <img src={voice.avatarUrl} alt="flagIcon" className="flagImages" />
                                            <Typography variant="body2" className="showcaseSubDescriptionItalic">
                                                {voice.labels.accent}
                                            </Typography>
                                        </div>
                                    </>
                                )}
                            </Box>
                            <Box sx={{ height: 40, display: 'flex', alignItems: 'center', gap: '2px' }}>
                                {playingVoiceId === voice.voice_id && ( 
                                    <>
                                        {barHeights.map((barMaxHeight, i) => {
                                            const isPlaying = playingVoiceId === voice.voice_id; 
                                            return (
                                                <motion.div
                                                    key={i}
                                                    initial={{ height: barMaxHeight * 0.5, opacity: 0.7 }}
                                                    animate={
                                                        isPlaying
                                                            ? {
                                                               height: barMaxHeight,
                                                                opacity: [0.7, 1, 0.7],
                                                                backgroundColor: ['#ECE7E7', '#C280FF'],
                                                            }
                                                            : { height: barMaxHeight * 0.5, opacity: 0.5 }
                                                    }
                                                    transition={
                                                        isPlaying
                                                            ? {
                                                                duration: 1,
                                                                repeat: Infinity,
                                                                repeatType: "reverse",
                                                                ease: "easeInOut",
                                                                delay: i * 0.07,
                                                            }
                                                            : { duration: 0.3 }
                                                    }
                                                    style={{
                                                        width: 2,
                                                        borderRadius: 2,
                                                        background: isPlaying ? "#C280FF" : "#B0B3C6",
                                                        marginLeft: i === 0 ? 0 : 1,
                                                        backgroundColor: '#F5F6FA',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                                    }}
                                                />
                                            );
                                        })}
                                    </>
                                )}
                            </Box>
                            <IconButton
                                edge="end"
                                aria-label="play"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePlayPauseVoice(voice);
                                }}
                            >
                                {playingVoiceId === voice.voice_id ?
                                    <PauseCircleOutlineOutlinedIcon sx={{color: '#EAEAED'}}/> : 
                                    <PlayCircleFilledWhiteOutlinedIcon sx={{color: '#EAEAED'}}/>
                                }
                            </IconButton>
                        </Box>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default VoiceSelector;